import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { ThemeProvider } from 'contexts/ThemeContext'
import { RouterProvider } from 'react-router-dom'
import { createAppRouter } from 'router'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'react-redux'
import { store } from 'redux/store'
import './lib/i18n'
import { amplifyConfig } from 'lib/cognito'
import { getCookieDomain } from 'lib/domain/getCookieDomain'
import { Amplify } from 'aws-amplify'
import { CookieStorage } from 'aws-amplify/utils'
// Use the scoped @aws-amplify package directly so this import lands in the same
// pre-bundle chunk as attemptCompleteOAuthFlow / cacheCognitoTokens (rather than
// the separate aws-amplify/auth/cognito re-export shim chunk).
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito'
import { setTreelessClass } from 'lib/treelessUtils'
import { outbox } from 'services/DiagnosticEventOutbox'
import { initRum } from 'lib/rum'

// Configure Amplify once at startup before React renders.
// useAmplifyAuth calling configure() again later with the same config is safe.
//
// Belt-and-suspenders: explicitly prime the token provider with the auth config
// BEFORE DefaultAmplify.configure() fires the OAuth listener. DefaultAmplify.configure()
// calls cognitoUserPoolsTokenProvider.setAuthConfig() on its own internally, but due to
// Vite pre-bundling it may call it on a different chunk's singleton than the one
// cacheCognitoTokens / tokenOrchestrator actually use. Calling it here (on the
// @aws-amplify/auth/cognito import, which is the same chunk as attemptCompleteOAuthFlow)
// ensures DefaultTokenStore.authConfig is non-null before storeTokens() is called.
if (amplifyConfig.Auth) {
  cognitoUserPoolsTokenProvider.setAuthConfig(amplifyConfig.Auth)
}
Amplify.configure(amplifyConfig)
const _mainHostname = window.location.hostname
const _mainCookieDomain = getCookieDomain(_mainHostname)
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: _mainCookieDomain,
    secure: window.location.protocol === 'https:',
  })
)
import { AuthProvider } from 'pages/login/use-auth-context'
import { setProjectId } from 'redux/settingsSlice'
import { AppModeProvider } from 'lib/mode/AppModeContext'

function getInitialProjectId(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('projectId') || undefined
    if (!id) return undefined
    const trimmed = id.trim()
    return trimmed.length ? trimmed : undefined
  } catch {
    return undefined
  }
}

const initialProjectId = getInitialProjectId()
if (initialProjectId) {
  store.dispatch(setProjectId(initialProjectId))
}

outbox.startBackground()
void outbox.flushAll()
initRum()

const rootElement = document.getElementById('root')
setTreelessClass()

if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Provider store={store}>
        <AppModeProvider>
          <ThemeProvider>
            <AuthProvider>
              <RouterProvider
                router={createAppRouter()}
                future={{ v7_startTransition: true }}
              />
            </AuthProvider>
          </ThemeProvider>
        </AppModeProvider>
        <Toaster />
      </Provider>
    </React.StrictMode>
  )
}
